import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Store, LogOut, Plus, Pencil, Trash2, Search, ArrowLeft, X, Save, Clock, Phone, Mail, Globe, MapPin } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { InquiriesPanel } from "../components/InquiriesPanel";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { API } from "../App";

const HTTP_UNAUTHORIZED = 401;

const CATEGORIES = [
  "Supermarket", "Fashion", "Optical", "Electronics", "Pharmacy & Wellness",
  "Home Furnishings", "Retail Technology", "Dental Clinic", "African Crafts & Souvenirs",
  "Home Decor", "Hair Salon", "Real Estate", "Jewellery", "Water Services",
  "M-Pesa Services", "Home Appliances", "Dermatology & Aesthetics"
];

export default function AdminDashboardPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTenant, setEditingTenant] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("tenants");
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  const fetchTenants = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tenants`);
      setTenants(res.data);
    } catch (e) {
      toast.error("Couldn't load tenants. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/inquiries`);
      setUnreadCount(res.data.filter((i) => !i.is_read).length);
    } catch (e) {
      // Non-fatal: badge simply won't update
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        await axios.get(`${API}/admin/me`);
      } catch (e) {
        navigate("/admin");
        return;
      }
      fetchTenants();
      fetchUnreadCount();
    };
    init();
  }, [navigate, fetchTenants, fetchUnreadCount]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (e) {
      // Ignore; still redirect to login
    }
    navigate("/admin");
  };

  const openNewTenant = () => {
    setEditingTenant({
      name: "",
      category: CATEGORIES[0],
      description: "",
      location: "",
      email: "",
      contacts: [{ name: "", phone: "", whatsapp: "" }],
      social_media: { website: "", instagram: "", facebook: "", tiktok: "" },
      business_hours: { weekdays: "", saturday: "", sunday: "", note: "" },
      offers: [],
      is_anchor: false,
      image_url: ""
    });
    setIsDialogOpen(true);
  };

  const openEditTenant = (tenant) => {
    setEditingTenant({
      ...tenant,
      contacts: tenant.contacts?.length > 0 ? tenant.contacts : [{ name: "", phone: "", whatsapp: "" }],
      social_media: tenant.social_media || { website: "", instagram: "", facebook: "", tiktok: "" },
      business_hours: tenant.business_hours || { weekdays: "", saturday: "", sunday: "", note: "" },
      offers: tenant.offers || []
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingTenant.name || !editingTenant.category) {
      toast.error("Name and category are required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...editingTenant,
        contacts: editingTenant.contacts.filter(c => c.phone),
        offers: editingTenant.offers.filter(o => o.trim())
      };

      if (editingTenant.id) {
        await axios.put(`${API}/admin/tenants/${editingTenant.id}`, payload);
        toast.success("Tenant updated successfully");
      } else {
        await axios.post(`${API}/admin/tenants`, payload);
        toast.success("Tenant created successfully");
      }
      
      setIsDialogOpen(false);
      setEditingTenant(null);
      fetchTenants();
    } catch (e) {
      if (e.response?.status === HTTP_UNAUTHORIZED) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error(e.response?.data?.detail || "Failed to save tenant");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      await axios.delete(`${API}/admin/tenants/${deleteTarget.id}`);
      toast.success("Tenant deleted successfully");
      setDeleteTarget(null);
      fetchTenants();
    } catch (e) {
      if (e.response?.status === HTTP_UNAUTHORIZED) {
        toast.error("Session expired. Please login again.");
        handleLogout();
      } else {
        toast.error("Failed to delete tenant");
      }
    }
  };

  const updateContact = (index, field, value) => {
    const newContacts = [...editingTenant.contacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setEditingTenant({ ...editingTenant, contacts: newContacts });
  };

  const addContact = () => {
    setEditingTenant({
      ...editingTenant,
      contacts: [...editingTenant.contacts, { name: "", phone: "", whatsapp: "" }]
    });
  };

  const removeContact = (index) => {
    if (editingTenant.contacts.length > 1) {
      setEditingTenant({
        ...editingTenant,
        contacts: editingTenant.contacts.filter((_, i) => i !== index)
      });
    }
  };

  const filteredTenants = tenants.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      {/* Header */}
      <header className="sticky top-0 z-50 glass glass-dark border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-home-link">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Store className="w-5 h-5 text-white" />
              </div>
            </Link>
            <div>
              <h1 className="font-heading font-bold text-lg leading-tight text-foreground">Admin Dashboard</h1>
              <p className="text-xs text-muted-foreground">Manage tenants</p>
            </div>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-border/50">
          <button
            onClick={() => setActiveTab("tenants")}
            data-testid="tab-tenants"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${activeTab === "tenants" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            Tenants
          </button>
          <button
            onClick={() => { setActiveTab("leads"); fetchUnreadCount(); }}
            data-testid="tab-leads"
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2 ${activeTab === "leads" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            Leads
            {unreadCount > 0 && (
              <span data-testid="unread-badge" className="px-1.5 py-0.5 rounded-full bg-primary text-white text-[10px] font-bold min-w-[18px] text-center">{unreadCount}</span>
            )}
          </button>
        </div>

        {activeTab === "leads" ? (
          <InquiriesPanel onLogout={handleLogout} onCountsChange={fetchUnreadCount} />
        ) : (
        <>
        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="admin-search-input"
              type="text"
              placeholder="Search tenants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button onClick={openNewTenant} data-testid="add-tenant-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Tenant
          </Button>
        </div>

        {/* Tenants List */}
        {loading ? (
          <div className="space-y-3">
            {Array(5).fill(0).map((_, i) => (
              <div key={i} className="bg-card rounded-xl h-20 animate-pulse border border-border/50" />
            ))}
          </div>
        ) : filteredTenants.length === 0 ? (
          <div className="text-center py-12">
            <Store className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold text-foreground mb-2">No tenants found</h3>
            <p className="text-muted-foreground text-sm mb-4">
              {searchQuery ? "Try a different search term" : "Add your first tenant to get started"}
            </p>
            {!searchQuery && (
              <Button onClick={openNewTenant}>
                <Plus className="w-4 h-4 mr-2" />
                Add Tenant
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTenants.map((tenant, index) => (
              <motion.div
                key={tenant.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.02, duration: 0.3 }}
                className="bg-card rounded-xl border border-border/50 p-4 flex items-center gap-4"
              >
                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0 bg-muted">
                  <img
                    src={tenant.image_url || "https://images.unsplash.com/photo-1635930376155-4ee50db13cca?w=200"}
                    alt={tenant.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-foreground truncate">{tenant.name}</h3>
                    {tenant.is_anchor && (
                      <span className="px-2 py-0.5 rounded-full bg-secondary/20 text-secondary-foreground text-[10px] font-bold uppercase shrink-0">
                        Anchor
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{tenant.category}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditTenant(tenant)}
                    data-testid={`edit-tenant-${tenant.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDeleteTarget(tenant)}
                    data-testid={`delete-tenant-${tenant.id}`}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
        </>
        )}
      </main>

      {/* Edit/Create Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0">
          <DialogHeader className="p-6 pb-0">
            <DialogTitle className="font-heading">
              {editingTenant?.id ? "Edit Tenant" : "Add New Tenant"}
            </DialogTitle>
          </DialogHeader>
          
          {editingTenant && (
            <ScrollArea className="max-h-[60vh] px-6">
              <div className="space-y-6 pb-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-foreground">Basic Information</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        data-testid="tenant-name-input"
                        value={editingTenant.name}
                        onChange={(e) => setEditingTenant({ ...editingTenant, name: e.target.value })}
                        placeholder="Store name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <select
                        id="category"
                        data-testid="tenant-category-select"
                        value={editingTenant.category}
                        onChange={(e) => setEditingTenant({ ...editingTenant, category: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      data-testid="tenant-description-input"
                      value={editingTenant.description || ""}
                      onChange={(e) => setEditingTenant({ ...editingTenant, description: e.target.value })}
                      placeholder="Brief description of the store"
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        value={editingTenant.location || ""}
                        onChange={(e) => setEditingTenant({ ...editingTenant, location: e.target.value })}
                        placeholder="e.g. Ground Floor"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={editingTenant.email || ""}
                        onChange={(e) => setEditingTenant({ ...editingTenant, email: e.target.value })}
                        placeholder="store@email.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="image_url">Image URL</Label>
                    <Input
                      id="image_url"
                      value={editingTenant.image_url || ""}
                      onChange={(e) => setEditingTenant({ ...editingTenant, image_url: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <Switch
                      id="is_anchor"
                      checked={editingTenant.is_anchor}
                      onCheckedChange={(checked) => setEditingTenant({ ...editingTenant, is_anchor: checked })}
                    />
                    <Label htmlFor="is_anchor">Anchor Tenant</Label>
                  </div>
                </div>

                <Separator />

                {/* Contacts */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-foreground">Contacts</h4>
                    <Button variant="outline" size="sm" onClick={addContact}>
                      <Plus className="w-3 h-3 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  {editingTenant.contacts.map((contact, idx) => (
                    <div key={idx} className="grid grid-cols-4 gap-2 items-end">
                      <Input
                        placeholder="Name"
                        value={contact.name || ""}
                        onChange={(e) => updateContact(idx, "name", e.target.value)}
                      />
                      <Input
                        placeholder="Phone *"
                        value={contact.phone || ""}
                        onChange={(e) => updateContact(idx, "phone", e.target.value)}
                      />
                      <Input
                        placeholder="WhatsApp"
                        value={contact.whatsapp || ""}
                        onChange={(e) => updateContact(idx, "whatsapp", e.target.value)}
                      />
                      {editingTenant.contacts.length > 1 && (
                        <Button variant="ghost" size="icon" onClick={() => removeContact(idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <Separator />

                {/* Business Hours */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-foreground">Business Hours</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Weekdays (Mon-Fri)</Label>
                      <Input
                        placeholder="e.g. 9:00 AM - 6:00 PM"
                        value={editingTenant.business_hours?.weekdays || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          business_hours: { ...editingTenant.business_hours, weekdays: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Saturday</Label>
                      <Input
                        placeholder="e.g. 9:00 AM - 4:00 PM"
                        value={editingTenant.business_hours?.saturday || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          business_hours: { ...editingTenant.business_hours, saturday: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sunday</Label>
                      <Input
                        placeholder="e.g. Closed"
                        value={editingTenant.business_hours?.sunday || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          business_hours: { ...editingTenant.business_hours, sunday: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Note</Label>
                      <Input
                        placeholder="e.g. Holiday hours may vary"
                        value={editingTenant.business_hours?.note || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          business_hours: { ...editingTenant.business_hours, note: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Social Media */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-foreground">Social Media</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Website</Label>
                      <Input
                        placeholder="https://..."
                        value={editingTenant.social_media?.website || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          social_media: { ...editingTenant.social_media, website: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram</Label>
                      <Input
                        placeholder="@handle"
                        value={editingTenant.social_media?.instagram || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          social_media: { ...editingTenant.social_media, instagram: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Facebook</Label>
                      <Input
                        placeholder="Page name"
                        value={editingTenant.social_media?.facebook || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          social_media: { ...editingTenant.social_media, facebook: e.target.value }
                        })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>TikTok</Label>
                      <Input
                        placeholder="@handle"
                        value={editingTenant.social_media?.tiktok || ""}
                        onChange={(e) => setEditingTenant({
                          ...editingTenant,
                          social_media: { ...editingTenant.social_media, tiktok: e.target.value }
                        })}
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Offers */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-foreground">What We Offer (comma separated)</h4>
                  <Input
                    placeholder="e.g. Hair Cuts, Styling, Coloring"
                    value={editingTenant.offers?.join(", ") || ""}
                    onChange={(e) => setEditingTenant({
                      ...editingTenant,
                      offers: e.target.value.split(",").map(s => s.trim())
                    })}
                  />
                </div>
              </div>
            </ScrollArea>
          )}

          <DialogFooter className="p-6 pt-0">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-tenant-button">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tenant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
